import { FormatHelpers } from "../helpers/format-helpers";
import { IChartContainer } from "../models/base/chart-container.interface";
import { ChartStyleSettingsModel } from "../models/chart-style/chart-style-settings.model";
import { Timeframe } from "../models/enum/timeframe.enum";
import { IComponent } from "./component.interface";

export class ChangeFromCloseLabelComponent implements IComponent {
    public id = '';

    private base: IChartContainer;
    private element: HTMLElement | null = null;
    private enabled = false;
    private value: number | null = null;
    private changePositive = false;
    private firstBarX: number | null = null;
    private lastBarX: number | null = null;
    private color = '';
    private firstCrossHairImage: any = null;
    private lastCrossHairImage: any = null;

    private active = true;
    private icon = '';

    constructor(base: IChartContainer) {
        this.base = base;
        this.subscribeChartService();
    }

    public update(value: number, changePositive: boolean, color: string, firstBarX: number, lastBarX: number) {
        this.value = value;
        this.changePositive = changePositive;
        this.color = color;
        this.firstBarX = firstBarX;
        this.lastBarX = lastBarX;
        this.show();
    }

    private updateCrossHairsIconsTimer: any;
    public show() {
        if (!this.enabled || this.value == null || !this.color || !this.base.tiChartSeries.lastDataPoint) {
            this.hide();
            return;
        }

        if (!this.base.pnpMode && !this.base.tiChartSeries?.chartStyleSettings?.chartSettingsPopupModel?.displayMainChartChangePercentVisibleCandles) {
            this.hide();
            return;
        }

        let cfc = Math.abs(parseFloat(this.value.toFixed(2)));
        let prefix = '';
        if (cfc != 0) {
            if (this.changePositive) {
                prefix = '+';
            } else {
                prefix = '-';
            }
        }

        const timespan = this.getTimespan();
        
        const className = `change-from-close-label ${this.active ? 'active' : ''} ${this.base.pnpMode ? 'pnp ' : ''} ${this.base.element.clientWidth > 200 ? '' : 'small'}`
        const changeLabelHtml = 
        `
            <div class="wrapper">
                <p style="color:${this.color};">${prefix}${FormatHelpers.numberToStringThousandSeparators(cfc, true, 2)}% <span>${timespan}</span></p>
            </div>
        `;
        if (this.element) {
            this.setElementTop();
            this.element.className  = className;
            this.element?.setInnerHtml(changeLabelHtml);
        } else {
            const div = document.createElement('DIV');
            div.className = className;
            div.innerHTML = changeLabelHtml;

            this.element = div;

            this.setElementTop();
            this.base.tiChartSeries.element.findFirst('.chart-container .main-chart')?.insertAdjacentElement('beforeend', div);
        }

        this.updateCrossHairsIcons();
        clearTimeout(this.updateCrossHairsIconsTimer);
        this.updateCrossHairsIconsTimer = setTimeout(() => {
            this.updateCrossHairsIcons();
        }, 300);
    }

    private setElementTop() {
        if (!this.element) return;

        const top = this.base.tiChartSeries?.highChartBuilder?.getChartSpacing()?.top ?? 0;
        if (top && !this.base.pnpMode) {
            this.element.setAttribute('style', `top: ${top}px;`);
        } else {
            this.element.setAttribute('style', ``);
        }
    }

    private getTimespan(): string {
        const firstPoint = this.base.tiChartSeries.tiChartPoints.find(x => x.xValue == this.firstBarX);
        const lastPoint = this.base.tiChartSeries.tiChartPoints.find(x => x.xValue == this.lastBarX);
        if (!firstPoint || !lastPoint) {
            return ``;
        }

        const differenceMs = lastPoint.start.getTime() - firstPoint.start.getTime();

        // Convert milliseconds to units
        let minutes = parseFloat((differenceMs / (1000 * 60)).toFixed(2));
        const hours = parseFloat((differenceMs / (1000 * 60 * 60)).toFixed(2));
        let days = parseFloat((differenceMs / (1000 * 60 * 60 * 24)).toFixed(2));
        const months = parseFloat((differenceMs / (1000 * 60 * 60 * 24 * 30)).toFixed(2)); // Approximate month
        const years = parseFloat((differenceMs / (1000 * 60 * 60 * 24 * 365)).toFixed(2)); // Approximate year

        if (this.base.tiChartSeries.currentTimeframe == Timeframe.Day) {
            days = (lastPoint.xValue - firstPoint.xValue) + 1;
        } else if (this.base.tiChartSeries.currentTimeframe == Timeframe.OneMinute) {
            minutes = (lastPoint.xValue - firstPoint.xValue) + 1;
        }

        if (minutes < 60) {
            const roundedMinutes = Math.floor(minutes);
            if (minutes === roundedMinutes) {
                return`In ${roundedMinutes} ${roundedMinutes > 1 ? `minutes` : `minute`}`;
            }
            
            const flooredMinutes = Math.floor(minutes);
            return  `In ${flooredMinutes}+ ${flooredMinutes > 1 ? `minutes` : `minute`}`;
        } else if (hours < 24) {
            const roundedHours = Math.floor(hours);
            if (hours === roundedHours) {
                return `In ${roundedHours} ${roundedHours > 1 ? `hours` : `hour`}`;
            }

            const canShowPlus = this.base.tiChartSeries.currentTimeframe != Timeframe.SixtyMinute;
            const flooredHours = Math.floor(hours);
            return  `In ${flooredHours}${canShowPlus ? '+' : ''} ${flooredHours > 1 ? `hours` : `hour`}`;
        } else if (days < 30) {
            const roundedDays = Math.floor(days);
            if (days === roundedDays) {
                return `In ${roundedDays} ${roundedDays > 1 ? `days` : `day`}`;
            }

            const canShowPlus = this.base.tiChartSeries.currentTimeframe != Timeframe.Day;
            const flooredDays = Math.floor(days);
            return  `In ${flooredDays}${canShowPlus ? '+' : ''} ${flooredDays > 1 ? `days` : `day`}`;
        } else if (months < 12) {
            const roundedMonths = Math.floor(months);
            if (months === roundedMonths) {
                return `In ${roundedMonths} ${roundedMonths > 1 ? `months` : `month`}`;
            }

            const canShowPlus = this.base.tiChartSeries.currentTimeframe != Timeframe.Month;
            const flooredMonths = Math.floor(months);
            return  `In ${flooredMonths}${canShowPlus ? '+' : ''} ${flooredMonths > 1 ? `months` : `month`}`;
        } else {
            const roundedYears = Math.floor(years);
            if (years === roundedYears) {
                return `In ${roundedYears} ${roundedYears > 1 ? `years` : `year`}`;
            }

            const flooredYears = Math.floor(years);
            return  `In ${flooredYears}+ ${flooredYears > 1 ? `years` : `year`}`;
        }
    }

    private updateCrossHairsIcons() {
        if (!this.base.pnpMode) return;

        const styles = this.base.tiChartSeries?.chartStyleSettings;
        const yAxis = this.base.tiChartSeries?.highStockChart?.yAxis?.[0];
        if (!yAxis || !styles || !this.base.tiChartSeries.lastDataPoint) {
            this.hide();
            return;
        }

        
        const series = this.base.tiChartSeries?.highStockChart?.series?.[0];
        if (!series) {
            return;
        }

        
        const firstBarPoint = series.points.find((x: any) => x?.x == this.firstBarX);
        const lastBarPoint = series.points.find((x: any) => x?.x == this.lastBarX);
        if (!firstBarPoint || !lastBarPoint) {
            return;
        }

        const icon = this.getIcon(styles.isDarkTheme);
        
        if (!this.base.element.querySelector('.cross-hairs-v2.first') && this.firstCrossHairImage || this.icon != icon) {
            this.destroyFirstBarIcon();
        }

        if (!this.base.element.querySelector('.cross-hairs-v2.last') && this.lastCrossHairImage || this.icon != icon) {
            this.destroyLastBarIcon();
        }        

        const firstX = firstBarPoint.plotX + this.base.tiChartSeries.highStockChart.plotLeft - 5;
        const firstY = firstBarPoint.plotY + this.base.tiChartSeries.highStockChart.plotTop - 5;
        if (this.firstCrossHairImage) {
            this.firstCrossHairImage.attr({x: firstX, y: firstY});
        } else {
            this.firstCrossHairImage = this.base.tiChartSeries.highStockChart
                .renderer.image(icon, firstX, firstY, 10, 10)
                .addClass('cross-hairs-v2')
                .addClass('first')
                .attr({
                    zIndex: 9,
                    fill: 'red'
                })
                .add();
        }

        const lastX = lastBarPoint.plotX + this.base.tiChartSeries.highStockChart.plotLeft - 5;
        const lastY = lastBarPoint.plotY + this.base.tiChartSeries.highStockChart.plotTop - 5;
        if (this.lastCrossHairImage) {
            this.lastCrossHairImage.attr({x: lastX, y: lastY});
        } else {
            this.lastCrossHairImage = this.base.tiChartSeries.highStockChart
                .renderer.image(icon, lastX, lastY, 10, 10)
                .addClass('cross-hairs-v2')
                .addClass('last')
                .attr({
                    zIndex: 9
                })
                .add();
        }

        this.icon = icon;
    }

    private destroyFirstBarIcon() {
        this.firstCrossHairImage?.destroy();
        this.firstCrossHairImage = null;
    }

    private destroyLastBarIcon() {
        this.lastCrossHairImage?.destroy();
        this.lastCrossHairImage = null;
    }    

    public hide() {
        if (!this.enabled) {
            return;
        }

        this.destroyFirstBarIcon();
        this.destroyLastBarIcon();

        this.element?.remove();
        this.element = null;
    }

    private subscribeChartService() {
        this.base.chartService.subscribeChartStyleData((chartStyle: ChartStyleSettingsModel) => {
            this.enabled = !this.base.pnpMode || chartStyle.pnpChangePercentEnabled;
            if (this.enabled) {
                this.show();
            } else {
                this.hide();
            }
        });
    }

    private getIcon(isDarkTheme: boolean): string {
        // SVG source code
        const svgSource =  `
            <svg id="_03" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 100 100">
            <defs>
            <style>
                .cls-1 {
                fill: ${isDarkTheme ? '#272728' : '#fff'};
                stroke: ${this.color};
                stroke-miterlimit: 10;
                stroke-width: 9px;
                }
        
                .cls-2 {
                fill: ${this.color};
                stroke-width: 0px;
                }
            </style>
            </defs>
            <circle class="cls-1" cx="50" cy="50" r="44.85" />
            <circle class="cls-2" cx="50" cy="50" r="14.32" />
        </svg>
        `;

        // Create a data URL from the SVG source code
        const dataUrl = 'data:image/svg+xml,' + encodeURIComponent(svgSource);
        return dataUrl;
    }
}